import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const AUTH_IMG =
  "https://images.pexels.com/photos/5709630/pexels-photo-5709630.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, authenticate } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [busy, setBusy] = useState(false);

  // form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("customer");

  // 2FA (admin TOTP)
  const [twofaCode, setTwofaCode] = useState("");
  const [challenge, setChallenge] = useState("");

  useEffect(() => {
    if (user) {
      const dest =
        user.role === "admin" ? "/admin" : user.role === "shop_owner" ? "/owner" : "/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (params.get("error") === "google") toast.error("Google sign-in failed. Try again.");
  }, [params]);

  const finish = (data) => {
    authenticate(data.token, data.user);
    toast.success(`Welcome, ${data.user.name.split(" ")[0]}!`);
    const dest =
      data.user.role === "admin"
        ? "/admin"
        : data.user.role === "shop_owner"
        ? "/owner"
        : "/dashboard";
    navigate(dest, { replace: true });
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setChallenge(data.challenge);
        setTwofaCode("");
        setMode("twofa");
        toast.info("Enter your 2FA code", { description: "From your authenticator app." });
      } else {
        finish(data);
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify2fa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { challenge, code: twofaCode });
      finish(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, role });
      finish(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, we've sent reset instructions.");
      setMode("login");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    localStorage.setItem("nd_google_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left editorial image */}
      <div className="relative hidden lg:block grain overflow-hidden">
        <img src={AUTH_IMG} alt="Local boutique" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-foreground/40" />
        <Link to="/" className="absolute top-8 left-8 z-10 inline-flex items-center justify-center rounded-2xl bg-background px-3 py-2">
          <img src="/logo.png" alt="NearbyDeals" className="h-9 w-auto" />
        </Link>
        <div className="absolute bottom-12 left-8 right-8 z-10 text-background">
          <h2 className="font-display font-extrabold text-5xl tracking-tighter leading-[0.95]">
            Every street has a story.
          </h2>
          <p className="mt-4 text-background/80 text-lg max-w-sm">
            Sign in to claim offers, chat with shopkeepers and support the businesses around you.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-5 sm:px-10 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex mb-10">
            <img src="/logo.png" alt="NearbyDeals" className="h-11 w-auto" />
          </Link>

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="font-display font-extrabold text-4xl tracking-tighter" data-testid="auth-title">
                {mode === "login" && "Welcome back"}
                {mode === "register" && "Create account"}
                {mode === "twofa" && "Two-factor auth"}
                {mode === "forgot" && "Reset password"}
              </h1>
              <p className="text-muted-foreground mt-2 mb-8">
                {mode === "login" && "Log in to your NearbyDeals account."}
                {mode === "register" && "Join and start discovering local offers."}
                {mode === "twofa" && "Enter the 6-digit code from your authenticator app."}
                {mode === "forgot" && "Enter your email to reset your password."}
              </p>

              {/* LOGIN */}
              {mode === "login" && (
                <form onSubmit={submitLogin} className="space-y-4">
                  <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="login-email" />
                  <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="login-password" />
                  <button type="button" onClick={() => setMode("forgot")} className="text-sm font-semibold text-primary hover:underline">
                    Forgot password?
                  </button>
                  <SubmitBtn busy={busy} testid="login-submit">Log in</SubmitBtn>
                </form>
              )}

              {/* REGISTER */}
              {mode === "register" && (
                <form onSubmit={submitRegister} className="space-y-4">
                  <Field icon={User} placeholder="Full name" value={name} onChange={setName} testid="register-name" />
                  <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="register-email" />
                  <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="register-password" />
                  <div className="flex gap-3">
                    {[
                      ["customer", "I'm shopping"],
                      ["shop_owner", "I own a shop"],
                    ].map(([val, label]) => (
                      <button
                        type="button"
                        key={val}
                        data-testid={`role-${val}`}
                        onClick={() => setRole(val)}
                        className={`flex-1 rounded-2xl border-2 py-3 text-sm font-semibold transition-colors ${
                          role === val ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <SubmitBtn busy={busy} testid="register-submit">Create account</SubmitBtn>
                </form>
              )}

              {/* 2FA (admin TOTP) */}
              {mode === "twofa" && (
                <div className="space-y-4">
                  <div className="flex justify-center" data-testid="twofa-group">
                    <InputOTP maxLength={6} value={twofaCode} onChange={setTwofaCode}>
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <button
                    data-testid="verify-2fa"
                    onClick={verify2fa}
                    disabled={busy || twofaCode.length < 6}
                    className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
                  >
                    Back to login
                  </button>
                </div>
              )}

              {/* FORGOT */}
              {mode === "forgot" && (
                <form onSubmit={forgot} className="space-y-4">
                  <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="forgot-email" />
                  <SubmitBtn busy={busy} testid="forgot-submit">Send reset link</SubmitBtn>
                </form>
              )}

              {/* Alt auth */}
              {(mode === "login" || mode === "register") && (
                <>
                  <div className="flex items-center gap-4 my-7">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-sm text-muted-foreground font-medium">or</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <button
                    data-testid="google-login"
                    onClick={googleLogin}
                    className="w-full flex items-center justify-center gap-2 rounded-full border border-border py-3 font-semibold hover:border-primary hover:text-primary transition-colors"
                  >
                    <GoogleIcon /> Continue with Google
                  </button>
                </>
              )}

              {/* Toggle */}
              <p className="mt-8 text-center text-sm text-muted-foreground">
                {mode === "login" || mode === "twofa" || mode === "forgot" ? (
                  <>
                    New here?{" "}
                    <button data-testid="switch-register" onClick={() => setMode("register")} className="font-bold text-primary hover:underline">
                      Create an account
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button data-testid="switch-login" onClick={() => setMode("login")} className="font-bold text-primary hover:underline">
                      Log in
                    </button>
                  </>
                )}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type = "text", placeholder, value, onChange, testid, disabled }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <input
        data-testid={testid}
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl bg-secondary border border-border pl-11 pr-4 py-3.5 font-medium focus:outline-none focus:border-primary transition-colors disabled:opacity-60"
      />
    </div>
  );
}

function SubmitBtn({ busy, children, testid }) {
  return (
    <button
      type="submit"
      data-testid={testid}
      disabled={busy}
      className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{children} <ArrowRight className="w-4 h-4" /></>}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  );
}
